import { useStores } from "../../stores/root-store";
import { observer } from "mobx-react";
import _, { toNumber } from "lodash";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "react-bootstrap";
import defaultImgNa from "../../resources/images/img-default.jpg";
import { IModal } from "../../stores";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { ShowAlert } from "../../utils/functions";
import { AppConfig } from "../../AppConfig";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus } from "@fortawesome/free-solid-svg-icons";
import { IAllergen } from "../../stores/allergen";

const ProductForm = observer(({ show, setShowModal }: IModal) => {
  const { productStore, allergenStore } = useStores();
  const {
    saveProduct,
    editProduct,
    fetchProducts,
    categories,
    isAddOnsRequest,
  } = productStore;
  const { allergens, fetchAllergens } = allergenStore;
  const [isFormValid, setIsFormValid] = useState(false);
  const [productImg, setProductImg] = useState<string | null>();
  const [productImgFile, setProductImgFile] = useState<File | null>();
  const [productName, setProductName] = useState<string>();
  const [description, setDescription] = useState<string>();
  const [categoryId, setCategoryId] = useState<number>(0);
  const [newCategory, setNewCategory] = useState<string>();
  const [sellPrice, setSellPrice] = useState<number>();
  const [prodAllergens, setProdAllergens] = useState<number[]>(
    editProduct?.allergens?.map((p) => {
      return p.allergenId;
    }) ?? []
  );

  const isEditRequest = editProduct != null && editProduct.productId > 0;

  useEffect(() => {
    const isValid =
      productImg &&
      productName &&
      description &&
      (categoryId || newCategory) &&
      sellPrice
        ? true
        : false;
    setIsFormValid(isValid);
  }, [
    productImg,
    productName,
    description,
    categoryId,
    newCategory,
    sellPrice,
  ]);

  useEffect(() => {
    if (editProduct && !isAddOnsRequest) {
      setProductName(editProduct.productName);
      setDescription(editProduct.description);
      setCategoryId(editProduct.categoryId);
      setSellPrice(editProduct.sellPrice);
      setProductImg(
        editProduct.productImagePath
          ? AppConfig.APIBaseUrl + editProduct.productImagePath
          : defaultImgNa
      );
    }
  }, [editProduct, isAddOnsRequest]);

  useEffect(() => {
    fetchAllergens();
  }, []);

  const allergenList = useMemo(() => allergens, [allergens]);

  const handleImageChage = (e: ChangeEvent<HTMLInputElement>) => {
    setProductImg(
      e.target.files ? URL.createObjectURL(e.target.files[0]) : null
    );
    setProductImgFile(e.target.files ? e.target.files[0] : null);
  };

  const submitCallback = useCallback((res: boolean) => {
    if (res) {
      setShowModal(false);
      fetchProducts();
      ShowAlert.Success(
        `Product ${editProduct?.productId ? "updated" : "added"} successfully.`
      );
    } else {
      ShowAlert.Error("Request failed. Please try again.");
    }
  }, []);

  const handleFormSubmit = () => {
    if (!isFormValid) {
      ShowAlert.Error("One or more field value missing.");
      return;
    }

    saveProduct(
      {
        productId: editProduct?.productId,
        productName,
        description,
        categoryId,
        newCategory,
        sellPrice,
        productImagePath: editProduct?.productImagePath,
        allergens: prodAllergens.map((a) => {
          return { allergenId: a };
        }),
      },
      productImgFile,
      submitCallback
    );
  };

  const handleAllergensCheck = (id: number, checked: boolean) => {
    if (checked) setProdAllergens((prev) => [...prev, id]);
    else setProdAllergens((prev) => prev.filter((p) => p !== id));
  };

  return (
    <Modal show={show} onHide={() => setShowModal(false)} className="modal-lg">
      <ModalHeader closeButton>
        <Modal.Title>
          <h5>{isEditRequest ? "Update" : "Add"} Product</h5>
        </Modal.Title>
      </ModalHeader>
      <ModalBody>
        <form>
          <div className="d-flex justify-content-center">
            <img
              src={productImg ?? defaultImgNa}
              alt="Product"
              className="object-cover mb-2 shadow-lg bg-body rounded"
              height={250}
            />
          </div>
          <h6 className="text-center">Product Image</h6>
          <div className="mb-3">
            <input
              className="form-control"
              type="file"
              id="formFile"
              placeholder="img"
              onChange={handleImageChage}
            />
          </div>
          <div className="row">
            <div className="col">
              <div className="form-floating mb-3">
                <select
                  className="form-select"
                  id="catSelect"
                  aria-label="Floating label select example"
                  aria-placeholder="Cate"
                  value={categoryId}
                  onChange={(e) => setCategoryId(toNumber(e.target.value))}
                  disabled={!!newCategory?.length}
                >
                  <option value="0">New Category</option>
                  {categories
                    ?.filter((p) => p.id > 0)
                    .map((c) => (
                      <option value={c.id}>{c.name}</option>
                    ))}
                </select>
                <label htmlFor="floatingSelect">Choose Product Category</label>
              </div>
            </div>

            <div className="col">
              <div className="form-floating mb-3">
                <input
                  type="text"
                  className="form-control"
                  id="categoryText"
                  placeholder="Cat"
                  disabled={categoryId > 0}
                  onChange={(e) => setNewCategory(e.target.value)}
                />
                <label htmlFor="categoryText">New Category</label>
              </div>
            </div>
          </div>
          <div className="form-floating mb-3">
            <input
              type="text"
              className="form-control"
              id="prodName"
              placeholder="Name"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
            />
            <label htmlFor="prodName">Product Name</label>
          </div>
          <div className="form-floating mb-3">
            <textarea
              className="form-control"
              id="prodDesc"
              style={{ height: "75px" }}
              placeholder="Desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            ></textarea>
            <label htmlFor="prodDesc">Product Description</label>
          </div>

          <div className="form-floating mb-3">
            <input
              type="number"
              className="form-control"
              id="sellPrice"
              placeholder="£"
              value={sellPrice}
              onChange={(e) => setSellPrice(toNumber(e.target.value))}
            />
            <label htmlFor="sellPrice">Selling Price</label>
          </div>

          <div className="mb-3">
            <h6>Allergens</h6>
            <div className="alert alert-primary">
              {allergenList?.map((a: IAllergen) => {
                return (
                  <div
                    className="form-check form-check-inline"
                    key={a.allergenId}
                  >
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`allergen${a.allergenId}`}
                      value={a.allergenId}
                      checked={prodAllergens.some((p) => p === a.allergenId)}
                      onChange={(e) =>
                        handleAllergensCheck(a.allergenId, e.target.checked)
                      }
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`allergen${a.allergenId}`}
                    >
                      {a.allergenName}
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        </form>
      </ModalBody>
      <ModalFooter>
        <button
          className="btn btn-default flex-fill"
          onClick={handleFormSubmit}
          disabled={!isFormValid}
        >
          {isEditRequest ? (
            <>
              <FontAwesomeIcon icon={faEdit} className="me-2" />
              <span>Update Product</span>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              <span>Add Product</span>
            </>
          )}
        </button>
      </ModalFooter>
    </Modal>
  );
});

export default ProductForm;
